/**
 ===============================================================================

 PIN over web - JavaScript client functions (TEST)

 System: PIN over web

 Description: JavaScript client version

 Copyright (C) 2014 Nets. All rights reserved.

 Revision History:
 ------------------------------------------------------------------------------
 YYYY-MM-DD  WHO  DETAILS
 ------------------------------------------------------------------------------
 2010-02-26  stk  Initial version (BBS AS)
 2014-02-17  stk  Added functions for getting customer PIN
 2014-04-10  stk  Changes for masked PAN
 2014-10-29  stk  Changes for set PIN
 2017-10-27  stk  Removed set_pan
 ==============================================================================
 * */

/*
eslint-disable

 */

/*
 * Template for bbspin object
 */
export default class Pin {
    clearPwk = '';
    clearPin = '';
    cryptVer = '';
    keyA = '';
    keyB = '';
    pan = '';
    expiry = '';
    mpan = '';
    ssn = '';
    rsaKey = '';
    encPwk = '';
    encPin = '';
    constructor(rsaKey: any) {
        this.rsaKey = rsaKey;
    }

    /*
     * Add keyB to bbspin object
     * Return true on success, otherwise return false
     */
    set_keyB = (keyB: any) => {
        if (undefined === keyB || keyB.length !== 32) {
            throw new Error('Error set_keyB, missing or incorrect input');
        }
        this.keyB = keyB;
        return true;
    };

    set_card = (pan: any, expiry: any) => {
        if (undefined === pan || pan.length < 10 || pan.length > 19) {
            throw new Error('Error set_card, missing or incorrect input');
        }

        if (undefined === expiry || expiry.length !== 4) {
            throw new Error('Error set_card, missing or incorrect input');
        }

        this.pan = pan;
        this.expiry = expiry;
        this.cryptVer = '05';
        return true;
    };

    /*
     * Set masked PAN to bbspin object, need for viewing a card PIN
     * when in a PCI compliant manner.
     * Return true on success, otherwise return false
     */
    set_masked_pan = (mpan: any) => {
        if (undefined === mpan || mpan.length < 20 || mpan.length > 40) {
            throw new Error('Error set_masked_pan, missing or incorrect input');
        }
        this.mpan = mpan;
        this.cryptVer = '03';
        return true;
    };

    /*
     * Add SSN to bbspin object, needed for viewing customer PIN
     * Return true on success, otherwise return false
     */
    set_ssn = (ssn: any) => {
        if (undefined === ssn || ssn.length < 10 || ssn.length > 20) {
            throw new Error('Error set_ssn, missing or incorrect input');
        }
        this.ssn = ssn;
        this.cryptVer = '02';
        return true;
    };

    /*
     * Add clear text PIN and encrypt before storing in bbspin object
     * Return true on success, otherwise return false
     */
    set_pin = (pin: any) => {
        if (undefined === pin || pin.length !== 4) {
            throw new Error('Error set_pin, missing or incorrect input');
        }
        this.cryptVer = '04';
        this.encrypt_pin(pin);

        return true;
    };

    /*
     * Add encPin to bbspin object
     * Return true on success, otherwise return false
     */
    set_encPin = (encPin: any) => {
        if (undefined === encPin || encPin.length !== 16) {
            throw new Error('Error set_encPin, missing or incorrect input');
        }
        this.encPin = encPin;
        return true;
    };

    /*
     * Get encrypted PWK
     */
    get_encPwk = () => {
        if (this.encPwk === '') {
            throw new Error('Error get_encPwk, missing value');
        }
        return this.encPwk;
    };

    /*
     * Get PIN
     */

    get_pin = () => {
        if (this.clearPin === '') {
            throw new Error('Error get_pin, missing value');
        }
        return this.clearPin;
    };

    /*
     * Reset bbspin variables
     */

    reset_var = () => {
        this.clearPin = '';
        this.clearPwk = '';
        this.cryptVer = '';
        this.keyA = '';
        this.keyB = '';
        this.pan = '';
        this.expiry = '';
        this.mpan = '';
        this.ssn = '';
        this.encPwk = '';
        this.encPin = '';
    };

    generateEncryptedFetchPinMessage = (keyB: any, maskedPan: any) => {
        this.gen_key();
        this.set_keyB(keyB);
        this.form_pwk();
        this.set_masked_pan(maskedPan);
        this.encrypt_pwk();
        return this.get_encPwk();
    };

    decryptReceivedPin = (encPin: any) => {
        this.set_encPin(encPin);
        this.decrypt_pin();
        return this.get_pin();
    };

    generateEncryptedSetPinMessage = (keyB: any, pin: any) => {
        this.gen_key();
        this.set_keyB(keyB);
        this.form_pwk();
        this.set_pin(pin);
        this.encrypt_pwk();
        return this.get_encPwk();
    };

    /*
     * Generate random client key
     */
    gen_key = () => {
        let i;
        let rng;
        let x;
        let keyA = '';

        rng_seed_time();

        if (undefined === this.keyA) {
            throw new Error('Error gen_key, missing expected field');
        }

        this.keyA = '';
        // @ts-ignore
        rng = new SecureRandom();
        x = [0];

        for (i = 0; i < 16; i++) {
            rng.nextBytes(x);
            keyA += String.fromCharCode(x[0]);
        }

        const keyHex = stringToHex(keyA);
        if (keyHex !== '' && keyHex.length === 32) {
            this.keyA = keyHex;
            return true;
        }
        return false;
    };

    /*
     * Form PIN Working Key, from keyA, keyB
     * Return true on success, otherwise return false
     */

    form_pwk = () => {
        let result = '';

        if (this.keyA === '' || this.keyB === '') {
            throw new Error('Error form_pwk, missing expected fields');
        }

        result = xor_hex(this.keyA, this.keyB);
        if (result === '') {
            return false;
        }
        this.clearPwk = result;
        return true;
    };

    /*
     * RSA encrypt PWK
     * Return true on success, otherwise return false;
     */
    encrypt_pwk = () => {
        let rsa;
        let encData;
        let msg;

        if (this.cryptVer === '' || this.rsaKey === '' || this.clearPwk === '') {
            throw new Error('Error encrypt_pwk, missing expected fields in form');
        }

        /* Create key block */
        const { cryptVer } = this;
        switch (cryptVer) {
            case '02':
                if (this.ssn === '') {
                    throw new Error('Error encrypt_pwk, missing expected fields in form');
                }
                msg = `${this.cryptVer},${this.clearPwk},${this.keyB},${this.ssn}`;
                break;

            case '03':
                if (this.mpan === '') {
                    throw new Error('Error encrypt_pwk, missing expected fields in form');
                }
                msg = `${this.cryptVer},${this.clearPwk},${this.keyB},${this.mpan}`;
                break;

            case '04':
                if (this.encPin === '') {
                    throw new Error('Error encrypt_pwk, missing expected fields in form');
                }
                msg = `${this.cryptVer},${this.clearPwk},${this.keyB},${this.encPin}`;
                break;

            case '05':
                if (this.pan === '' || this.expiry === '' || this.clearPwk === '') {
                    throw new Error('Error encrypt_pwk, missing expected fields in form');
                }
                msg = `${this.cryptVer},${this.clearPwk},${this.pan},${this.expiry}`;
                break;
            default:
                throw new Error('Invalid cryptVer');
        }
        // @ts-ignore
        rsa = new RSAKey();
        rsa.setPublic(this.rsaKey, '03');
        encData = rsa.encrypt(msg);
        if (encData) {
            this.encPwk = encData;
            return true;
        }
        return false;
    };

    /*
     * Decrypt received PIN block
     * Return true on success, otherwise return false
     */

    decrypt_pin = () => {
        let pan = '0000';
        let desKey = '';
        let clearPinBlock = '';
        let decPinBlock = '';
        let encPinBlock = '';
        let i = 0;

        if (this.clearPwk === '' || this.encPin === '' || this.cryptVer === '') {
            throw new Error('Error decrypt_pin, missing expected fields');
        }
        if (this.encPin.length !== 16) {
            throw new Error('Error decrypt_pin, wrong length of encPin');
        }

        /* Assemble DES key */
        for (i = 0; i < 16; i += 2) {
            desKey += String.fromCharCode(parseInt(this.clearPwk.substr(i, 2), 16));
        }
        for (i = 16; i < 32; i += 2) {
            desKey += String.fromCharCode(parseInt(this.clearPwk.substr(i, 2), 16));
        }
        for (i = 0; i < 16; i += 2) {
            desKey += String.fromCharCode(parseInt(this.clearPwk.substr(i, 2), 16));
        }
        /* Assemble PIN block */
        for (i = 0; i < 16; i += 2) {
            encPinBlock += String.fromCharCode(parseInt(this.encPin.substr(i, 2), 16));
        }

        this.clearPin = '';
        if (this.cryptVer === '01') {
            if (this.pan === '') {
                throw new Error('Error decrypt_pin, missing expected fields');
            }
            /* ISO format 0 */
            if (this.pan.length === 16) {
                pan += this.pan.substr(3, 12);
            } else {
                throw new Error('Error decrypt_pin, not expected length of pan');
            }
            // @ts-ignore
            decPinBlock = des(desKey, encPinBlock, 0);
            clearPinBlock = xor_hex(stringToHex(decPinBlock), pan);

            if (clearPinBlock.substr(0, 2) === '04' && clearPinBlock.substr(6, 10) === 'FFFFFFFFFF') {
                this.clearPin += clearPinBlock.substr(2, 4);
                return true;
            }
        }
        if (this.cryptVer === '02' || this.cryptVer === '03') {
            /* ISO format 1 */
            // @ts-ignore
            decPinBlock = des(desKey, encPinBlock, 0);
            clearPinBlock = stringToHex(decPinBlock);

            if (clearPinBlock.substr(0, 2) === '14') {
                this.clearPin += clearPinBlock.substr(2, 4);
                return true;
            }
        }

        this.reset_var();
        return false;
    };

    /*
     * Encrypt clear PIN
     * Return true on success, otherwise return false
     */
    encrypt_pin = (pin: any) => {
        rng_seed_time();

        if (this.clearPwk === '' || pin === '') {
            throw new Error('Error encrypt_pin, missing expected fields');
        }
        if (pin.length !== 4) {
            throw new Error('Error encrypt_pin, wrong length of clearPin');
        }

        /* Assemble DES key */
        let desKey = '';
        for (let i = 0; i < 16; i += 2) {
            desKey += String.fromCharCode(parseInt(this.clearPwk.substr(i, 2), 16));
        }
        for (let i = 16; i < 32; i += 2) {
            desKey += String.fromCharCode(parseInt(this.clearPwk.substr(i, 2), 16));
        }
        for (let i = 0; i < 16; i += 2) {
            desKey += String.fromCharCode(parseInt(this.clearPwk.substr(i, 2), 16));
        }
        /* Assemble PIN block - ISO 1 */
        let isoBlock = String.fromCharCode(0x14);
        for (let i = 0; i < 4; i += 2) {
            isoBlock += String.fromCharCode(parseInt(pin.substr(i, 2), 16));
        }

        /* Generate random padding */
        // @ts-ignore
        let rng = new SecureRandom();
        let x = [0];
        for (let i = 3; i < 8; i++) {
            rng.nextBytes(x);
            isoBlock += String.fromCharCode(x[0]);
        }
        if (isoBlock.length !== 8) {
            throw new Error('Error encrypt_pin, wrong PIN block length');
        }

        this.encPin = '';
        // @ts-ignore
        this.encPin = stringToHex(des(desKey, isoBlock, 1));
        return true;
    };
}

/*
 * Convert Binary string to Hex stringToHex
 */
const stringToHex = (inputString: any) => {
    const hex = '0123456789ABCDEF';
    let result = '';
    const string = inputString;
    let i = 0;

    if (string.length === undefined) {
        throw new Error('Error stringToHex, input error');
    }

    /* Expand string to Hex */
    for (i = 0; i < string.length; i++) {
        result += hex.charAt(string.charCodeAt(i) >>> 4);
        result += hex.charAt(string.charCodeAt(i) & 0xf);
    }
    return result;
};

/*
 * Xor hex components comp1 and comp2, must have same length
 * Return result or "" on error
 */
const xor_hex = (comp1: any, comp2: any) => {
    let hex1 = 0;
    let hex2 = 0;
    let i = 0;
    let result = '';
    const patt = /[^0-9,a-f]/i; /* check for only hex char */

    if (undefined === comp1.length || undefined === comp2.length || comp1.length !== comp2.length) {
        throw new Error('Error xor_hex, input validation failed');
    }
    /* Check that input is only hex */
    if (comp1.match(patt) != null || comp2.match(patt) != null) {
        throw new Error('Error xor_hex, input not hex');
    }

    /* Xor each char */
    for (i = 0; i < comp1.length; i += 2) {
        hex1 = parseInt(comp1.substr(i, 2), 16);
        hex2 = parseInt(comp2.substr(i, 2), 16);
        if (Number.NaN === hex1 || Number.NaN === hex2) {
            throw new Error(`Error xor_hex, not a number occured, i: ${i.toString()}`);
        }
        const res = String.fromCharCode(hex1 ^ hex2);
        result += stringToHex(res);
    }
    return result;
};

const isPinTooSimple = (pin: any) => {
    // Reject 3 increasing or decreasing consecutive digits
    return new RegExp('(012|123|234|345|456|567|678|789|890|901|098|987|876|765|654|543|432|321|210|109)').test(pin);
};

/*---------------------------------------------
 * Other libs - DES
 *---------------------------------------------*/
// Paul Tero, July 2001
// http://www.tero.co.uk/des/
//
// Optimised for performance with large blocks by Michael Hayworth, November 2001
// http://www.netdealing.com
//
// THIS SOFTWARE IS PROVIDED "AS IS" AND
// ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
// IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE
// ARE DISCLAIMED.  IN NO EVENT SHALL THE AUTHOR OR CONTRIBUTORS BE LIABLE
// FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL
// DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS
// OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION)
// HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
// LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
// OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF
// SUCH DAMAGE.

// des
// this takes the key, the message, and whether to encrypt or decrypt
function des(key: any, message: any, encrypt: any, mode: any, iv: any, padding: any) {
    // declaring this locally speeds things up a bit
    const spfunction1 = [
        0x1010400, 0, 0x10000, 0x1010404, 0x1010004, 0x10404, 0x4, 0x10000, 0x400, 0x1010400, 0x1010404, 0x400,
        0x1000404, 0x1010004, 0x1000000, 0x4, 0x404, 0x1000400, 0x1000400, 0x10400, 0x10400, 0x1010000, 0x1010000,
        0x1000404, 0x10004, 0x1000004, 0x1000004, 0x10004, 0, 0x404, 0x10404, 0x1000000, 0x10000, 0x1010404, 0x4,
        0x1010000, 0x1010400, 0x1000000, 0x1000000, 0x400, 0x1010004, 0x10000, 0x10400, 0x1000004, 0x400, 0x4,
        0x1000404, 0x10404, 0x1010404, 0x10004, 0x1010000, 0x1000404, 0x1000004, 0x404, 0x10404, 0x1010400, 0x404,
        0x1000400, 0x1000400, 0, 0x10004, 0x10400, 0, 0x1010004,
    ];
    const spfunction2 = [
        -0x7fef7fe0, -0x7fff8000, 0x8000, 0x108020, 0x100000, 0x20, -0x7fefffe0, -0x7fff7fe0, -0x7fffffe0, -0x7fef7fe0,
        -0x7fef8000, -0x80000000, -0x7fff8000, 0x100000, 0x20, -0x7fefffe0, 0x108000, 0x100020, -0x7fff7fe0, 0,
        -0x80000000, 0x8000, 0x108020, -0x7ff00000, 0x100020, -0x7fffffe0, 0, 0x108000, 0x8020, -0x7fef8000,
        -0x7ff00000, 0x8020, 0, 0x108020, -0x7fefffe0, 0x100000, -0x7fff7fe0, -0x7ff00000, -0x7fef8000, 0x8000,
        -0x7ff00000, -0x7fff8000, 0x20, -0x7fef7fe0, 0x108020, 0x20, 0x8000, -0x80000000, 0x8020, -0x7fef8000, 0x100000,
        -0x7fffffe0, 0x100020, -0x7fff7fe0, -0x7fffffe0, 0x100020, 0x108000, 0, -0x7fff8000, 0x8020, -0x80000000,
        -0x7fefffe0, -0x7fef7fe0, 0x108000,
    ];
    const spfunction3 = [
        0x208, 0x8020200, 0, 0x8020008, 0x8000200, 0, 0x20208, 0x8000200, 0x20008, 0x8000008, 0x8000008, 0x20000,
        0x8020208, 0x20008, 0x8020000, 0x208, 0x8000000, 0x8, 0x8020200, 0x200, 0x20200, 0x8020000, 0x8020008, 0x20208,
        0x8000208, 0x20200, 0x20000, 0x8000208, 0x8, 0x8020208, 0x200, 0x8000000, 0x8020200, 0x8000000, 0x20008, 0x208,
        0x20000, 0x8020200, 0x8000200, 0, 0x200, 0x20008, 0x8020208, 0x8000200, 0x8000008, 0x200, 0, 0x8020008,
        0x8000208, 0x20000, 0x8000000, 0x8020208, 0x8, 0x20208, 0x20200, 0x8000008, 0x8020000, 0x8000208, 0x208,
        0x8020000, 0x20208, 0x8, 0x8020008, 0x20200,
    ];
    const spfunction4 = [
        0x802001, 0x2081, 0x2081, 0x80, 0x802080, 0x800081, 0x800001, 0x2001, 0, 0x802000, 0x802000, 0x802081, 0x81, 0,
        0x800080, 0x800001, 0x1, 0x2000, 0x800000, 0x802001, 0x80, 0x800000, 0x2001, 0x2080, 0x800081, 0x1, 0x2080,
        0x800080, 0x2000, 0x802080, 0x802081, 0x81, 0x800080, 0x800001, 0x802000, 0x802081, 0x81, 0, 0, 0x802000,
        0x2080, 0x800080, 0x800081, 0x1, 0x802001, 0x2081, 0x2081, 0x80, 0x802081, 0x81, 0x1, 0x2000, 0x800001, 0x2001,
        0x802080, 0x800081, 0x2001, 0x2080, 0x800000, 0x802001, 0x80, 0x800000, 0x2000, 0x802080,
    ];
    const spfunction5 = [
        0x100, 0x2080100, 0x2080000, 0x42000100, 0x80000, 0x100, 0x40000000, 0x2080000, 0x40080100, 0x80000, 0x2000100,
        0x40080100, 0x42000100, 0x42080000, 0x80100, 0x40000000, 0x2000000, 0x40080000, 0x40080000, 0, 0x40000100,
        0x42080100, 0x42080100, 0x2000100, 0x42080000, 0x40000100, 0, 0x42000000, 0x2080100, 0x2000000, 0x42000000,
        0x80100, 0x80000, 0x42000100, 0x100, 0x2000000, 0x40000000, 0x2080000, 0x42000100, 0x40080100, 0x2000100,
        0x40000000, 0x42080000, 0x2080100, 0x40080100, 0x100, 0x2000000, 0x42080000, 0x42080100, 0x80100, 0x42000000,
        0x42080100, 0x2080000, 0, 0x40080000, 0x42000000, 0x80100, 0x2000100, 0x40000100, 0x80000, 0, 0x40080000,
        0x2080100, 0x40000100,
    ];
    const spfunction6 = [
        0x20000010, 0x20400000, 0x4000, 0x20404010, 0x20400000, 0x10, 0x20404010, 0x400000, 0x20004000, 0x404010,
        0x400000, 0x20000010, 0x400010, 0x20004000, 0x20000000, 0x4010, 0, 0x400010, 0x20004010, 0x4000, 0x404000,
        0x20004010, 0x10, 0x20400010, 0x20400010, 0, 0x404010, 0x20404000, 0x4010, 0x404000, 0x20404000, 0x20000000,
        0x20004000, 0x10, 0x20400010, 0x404000, 0x20404010, 0x400000, 0x4010, 0x20000010, 0x400000, 0x20004000,
        0x20000000, 0x4010, 0x20000010, 0x20404010, 0x404000, 0x20400000, 0x404010, 0x20404000, 0, 0x20400010, 0x10,
        0x4000, 0x20400000, 0x404010, 0x4000, 0x400010, 0x20004010, 0, 0x20404000, 0x20000000, 0x400010, 0x20004010,
    ];
    const spfunction7 = [
        0x200000, 0x4200002, 0x4000802, 0, 0x800, 0x4000802, 0x200802, 0x4200800, 0x4200802, 0x200000, 0, 0x4000002,
        0x2, 0x4000000, 0x4200002, 0x802, 0x4000800, 0x200802, 0x200002, 0x4000800, 0x4000002, 0x4200000, 0x4200800,
        0x200002, 0x4200000, 0x800, 0x802, 0x4200802, 0x200800, 0x2, 0x4000000, 0x200800, 0x4000000, 0x200800, 0x200000,
        0x4000802, 0x4000802, 0x4200002, 0x4200002, 0x2, 0x200002, 0x4000000, 0x4000800, 0x200000, 0x4200800, 0x802,
        0x200802, 0x4200800, 0x802, 0x4000002, 0x4200802, 0x4200000, 0x200800, 0, 0x2, 0x4200802, 0, 0x200802,
        0x4200000, 0x800, 0x4000002, 0x4000800, 0x800, 0x200002,
    ];
    const spfunction8 = [
        0x10001040, 0x1000, 0x40000, 0x10041040, 0x10000000, 0x10001040, 0x40, 0x10000000, 0x40040, 0x10040000,
        0x10041040, 0x41000, 0x10041000, 0x41040, 0x1000, 0x40, 0x10040000, 0x10000040, 0x10001000, 0x1040, 0x41000,
        0x40040, 0x10040040, 0x10041000, 0x1040, 0, 0, 0x10040040, 0x10000040, 0x10001000, 0x41040, 0x40000, 0x41040,
        0x40000, 0x10041000, 0x1000, 0x40, 0x10040040, 0x1000, 0x41040, 0x10001000, 0x40, 0x10000040, 0x10040000,
        0x10040040, 0x10000000, 0x40000, 0x10001040, 0, 0x10041040, 0x40040, 0x10000040, 0x10040000, 0x10001000,
        0x10001040, 0, 0x10041040, 0x41000, 0x41000, 0x1040, 0x1040, 0x40040, 0x10000000, 0x10041000,
    ];

    // create the 16 or 48 subkeys we will need
    const keys = des_createKeys(key);
    let m = 0;
    let i;
    let j;
    let temp;
    let temp2;
    let right1;
    let right2;
    let left;
    let right;
    let looping;
    let cbcleft;
    let cbcleft2;
    let cbcright;
    let cbcright2;
    let endloop;
    let loopinc;
    let len = message.length;
    let chunk = 0;
    // set up the loops for single and triple des
    const iterations = keys.length === 32 ? 3 : 9; // single or triple des
    if (iterations === 3) {
        looping = encrypt ? [0, 32, 2] : [30, -2, -2];
    } else {
        looping = encrypt ? [0, 32, 2, 62, 30, -2, 64, 96, 2] : [94, 62, -2, 32, 64, 2, 30, -2, -2];
    }

    // pad the message depending on the padding parameter
    if (padding === 2) message += '        ';
    // pad the message with spaces
    else if (padding === 1) {
        temp = 8 - (len % 8);
        message += String.fromCharCode(temp, temp, temp, temp, temp, temp, temp, temp);
        if (temp === 8) len += 8;
    } // PKCS7 padding
    else if (!padding) message += '\0\0\0\0\0\0\0\0'; // pad the message out with null bytes

    // store the result here
    let result = '';
    let tempresult = '';

    if (mode === 1) {
        // CBC mode
        cbcleft =
            (iv.charCodeAt(m++) << 24) | (iv.charCodeAt(m++) << 16) | (iv.charCodeAt(m++) << 8) | iv.charCodeAt(m++);
        cbcright =
            (iv.charCodeAt(m++) << 24) | (iv.charCodeAt(m++) << 16) | (iv.charCodeAt(m++) << 8) | iv.charCodeAt(m++);
        m = 0;
    }

    // loop through each 64 bit chunk of the message
    while (m < len) {
        left =
            (message.charCodeAt(m++) << 24) |
            (message.charCodeAt(m++) << 16) |
            (message.charCodeAt(m++) << 8) |
            message.charCodeAt(m++);
        right =
            (message.charCodeAt(m++) << 24) |
            (message.charCodeAt(m++) << 16) |
            (message.charCodeAt(m++) << 8) |
            message.charCodeAt(m++);

        // for Cipher Block Chaining mode, xor the message with the previous result
        if (mode === 1) {
            if (encrypt) {
                // @ts-ignore
                left ^= cbcleft;
                // @ts-ignore
                right ^= cbcright;
            } else {
                cbcleft2 = cbcleft;
                cbcright2 = cbcright;
                cbcleft = left;
                cbcright = right;
            }
        }

        // first each 64 but chunk of the message must be permuted according to IP
        temp = ((left >>> 4) ^ right) & 0x0f0f0f0f;
        right ^= temp;
        left ^= temp << 4;
        temp = ((left >>> 16) ^ right) & 0x0000ffff;
        right ^= temp;
        left ^= temp << 16;
        temp = ((right >>> 2) ^ left) & 0x33333333;
        left ^= temp;
        right ^= temp << 2;
        temp = ((right >>> 8) ^ left) & 0x00ff00ff;
        left ^= temp;
        right ^= temp << 8;
        temp = ((left >>> 1) ^ right) & 0x55555555;
        right ^= temp;
        left ^= temp << 1;

        left = (left << 1) | (left >>> 31);
        right = (right << 1) | (right >>> 31);

        // do this either 1 or 3 times for each chunk of the message
        for (j = 0; j < iterations; j += 3) {
            endloop = looping[j + 1];
            loopinc = looping[j + 2];
            // now go through and perform the encryption or decryption
            for (i = looping[j]; i !== endloop; i += loopinc) {
                // for efficiency
                right1 = right ^ keys[i];
                right2 = ((right >>> 4) | (right << 28)) ^ keys[i + 1];
                // the result is attained by passing these bytes through the S selection functions
                temp = left;
                left = right;
                right =
                    temp ^
                    (spfunction2[(right1 >>> 24) & 0x3f] |
                        spfunction4[(right1 >>> 16) & 0x3f] |
                        spfunction6[(right1 >>> 8) & 0x3f] |
                        spfunction8[right1 & 0x3f] |
                        spfunction1[(right2 >>> 24) & 0x3f] |
                        spfunction3[(right2 >>> 16) & 0x3f] |
                        spfunction5[(right2 >>> 8) & 0x3f] |
                        spfunction7[right2 & 0x3f]);
            }
            temp = left;
            left = right;
            right = temp; // unreverse left and right
        } // for either 1 or 3 iterations

        // move then each one bit to the right
        left = (left >>> 1) | (left << 31);
        right = (right >>> 1) | (right << 31);

        // now perform IP-1, which is IP in the opposite direction
        temp = ((left >>> 1) ^ right) & 0x55555555;
        right ^= temp;
        left ^= temp << 1;
        temp = ((right >>> 8) ^ left) & 0x00ff00ff;
        left ^= temp;
        right ^= temp << 8;
        temp = ((right >>> 2) ^ left) & 0x33333333;
        left ^= temp;
        right ^= temp << 2;
        temp = ((left >>> 16) ^ right) & 0x0000ffff;
        right ^= temp;
        left ^= temp << 16;
        temp = ((left >>> 4) ^ right) & 0x0f0f0f0f;
        right ^= temp;
        left ^= temp << 4;

        // for Cipher Block Chaining mode, xor the message with the previous result
        if (mode === 1) {
            if (encrypt) {
                cbcleft = left;
                cbcright = right;
            } else {
                // @ts-ignore
                left ^= cbcleft2;
                // @ts-ignore
                right ^= cbcright2;
            }
        }
        tempresult += String.fromCharCode(
            left >>> 24,
            (left >>> 16) & 0xff,
            (left >>> 8) & 0xff,
            left & 0xff,
            right >>> 24,
            (right >>> 16) & 0xff,
            (right >>> 8) & 0xff,
            right & 0xff,
        );

        chunk += 8;
        if (chunk === 512) {
            result += tempresult;
            tempresult = '';
            chunk = 0;
        }
    } // for every 8 characters, or 64 bits in the message

    // return the result as an array
    return result + tempresult;
} // end of des

// des_createKeys
// this takes as input a 64 bit key (even though only 56 bits are used)
// as an array of 2 integers, and returns 16 48 bit keys
function des_createKeys(key: any) {
    // declaring this locally speeds things up a bit
    const pc2bytes0 = [
        0, 0x4, 0x20000000, 0x20000004, 0x10000, 0x10004, 0x20010000, 0x20010004, 0x200, 0x204, 0x20000200, 0x20000204,
        0x10200, 0x10204, 0x20010200, 0x20010204,
    ];
    const pc2bytes1 = [
        0, 0x1, 0x100000, 0x100001, 0x4000000, 0x4000001, 0x4100000, 0x4100001, 0x100, 0x101, 0x100100, 0x100101,
        0x4000100, 0x4000101, 0x4100100, 0x4100101,
    ];
    const pc2bytes2 = [
        0, 0x8, 0x800, 0x808, 0x1000000, 0x1000008, 0x1000800, 0x1000808, 0, 0x8, 0x800, 0x808, 0x1000000, 0x1000008,
        0x1000800, 0x1000808,
    ];
    const pc2bytes3 = [
        0, 0x200000, 0x8000000, 0x8200000, 0x2000, 0x202000, 0x8002000, 0x8202000, 0x20000, 0x220000, 0x8020000,
        0x8220000, 0x22000, 0x222000, 0x8022000, 0x8222000,
    ];
    const pc2bytes4 = [
        0, 0x40000, 0x10, 0x40010, 0, 0x40000, 0x10, 0x40010, 0x1000, 0x41000, 0x1010, 0x41010, 0x1000, 0x41000, 0x1010,
        0x41010,
    ];
    const pc2bytes5 = [
        0, 0x400, 0x20, 0x420, 0, 0x400, 0x20, 0x420, 0x2000000, 0x2000400, 0x2000020, 0x2000420, 0x2000000, 0x2000400,
        0x2000020, 0x2000420,
    ];
    const pc2bytes6 = [
        0, 0x10000000, 0x80000, 0x10080000, 0x2, 0x10000002, 0x80002, 0x10080002, 0, 0x10000000, 0x80000, 0x10080000,
        0x2, 0x10000002, 0x80002, 0x10080002,
    ];
    const pc2bytes7 = [
        0, 0x10000, 0x800, 0x10800, 0x20000000, 0x20010000, 0x20000800, 0x20010800, 0x20000, 0x30000, 0x20800, 0x30800,
        0x20020000, 0x20030000, 0x20020800, 0x20030800,
    ];
    const pc2bytes8 = [
        0, 0x40000, 0, 0x40000, 0x2, 0x40002, 0x2, 0x40002, 0x2000000, 0x2040000, 0x2000000, 0x2040000, 0x2000002,
        0x2040002, 0x2000002, 0x2040002,
    ];
    const pc2bytes9 = [
        0, 0x10000000, 0x8, 0x10000008, 0, 0x10000000, 0x8, 0x10000008, 0x400, 0x10000400, 0x408, 0x10000408, 0x400,
        0x10000400, 0x408, 0x10000408,
    ];
    const pc2bytes10 = [
        0, 0x20, 0, 0x20, 0x100000, 0x100020, 0x100000, 0x100020, 0x2000, 0x2020, 0x2000, 0x2020, 0x102000, 0x102020,
        0x102000, 0x102020,
    ];
    const pc2bytes11 = [
        0, 0x1000000, 0x200, 0x1000200, 0x200000, 0x1200000, 0x200200, 0x1200200, 0x4000000, 0x5000000, 0x4000200,
        0x5000200, 0x4200000, 0x5200000, 0x4200200, 0x5200200,
    ];
    const pc2bytes12 = [
        0, 0x1000, 0x8000000, 0x8001000, 0x80000, 0x81000, 0x8080000, 0x8081000, 0x10, 0x1010, 0x8000010, 0x8001010,
        0x80010, 0x81010, 0x8080010, 0x8081010,
    ];
    const pc2bytes13 = [0, 0x4, 0x100, 0x104, 0, 0x4, 0x100, 0x104, 0x1, 0x5, 0x101, 0x105, 0x1, 0x5, 0x101, 0x105];

    // how many iterations (1 for des, 3 for triple des)
    const iterations = key.length > 8 ? 3 : 1; // changed by Paul 16/6/2007 to use Triple DES for 9+ byte keys
    // stores the return keys
    const keys = new Array(32 * iterations);
    // now define the left shifts which need to be done
    const shifts = [0, 0, 1, 1, 1, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 0];
    // other variables
    let lefttemp;
    let righttemp;
    let m = 0;
    let n = 0;
    let temp;

    for (let j = 0; j < iterations; j++) {
        // either 1 or 3 iterations
        let left =
            (key.charCodeAt(m++) << 24) |
            (key.charCodeAt(m++) << 16) |
            (key.charCodeAt(m++) << 8) |
            key.charCodeAt(m++);
        let right =
            (key.charCodeAt(m++) << 24) |
            (key.charCodeAt(m++) << 16) |
            (key.charCodeAt(m++) << 8) |
            key.charCodeAt(m++);

        temp = ((left >>> 4) ^ right) & 0x0f0f0f0f;
        right ^= temp;
        left ^= temp << 4;
        temp = ((right >>> -16) ^ left) & 0x0000ffff;
        left ^= temp;
        right ^= temp << -16;
        temp = ((left >>> 2) ^ right) & 0x33333333;
        right ^= temp;
        left ^= temp << 2;
        temp = ((right >>> -16) ^ left) & 0x0000ffff;
        left ^= temp;
        right ^= temp << -16;
        temp = ((left >>> 1) ^ right) & 0x55555555;
        right ^= temp;
        left ^= temp << 1;
        temp = ((right >>> 8) ^ left) & 0x00ff00ff;
        left ^= temp;
        right ^= temp << 8;
        temp = ((left >>> 1) ^ right) & 0x55555555;
        right ^= temp;
        left ^= temp << 1;

        // the right side needs to be shifted and to get the last four bits of the left side
        temp = (left << 8) | ((right >>> 20) & 0x000000f0);
        // left needs to be put upside down
        left = (right << 24) | ((right << 8) & 0xff0000) | ((right >>> 8) & 0xff00) | ((right >>> 24) & 0xf0);
        right = temp;

        // now go through and perform these shifts on the left and right keys
        for (let i = 0; i < shifts.length; i++) {
            // shift the keys either one or two bits to the left
            if (shifts[i]) {
                left = (left << 2) | (left >>> 26);
                right = (right << 2) | (right >>> 26);
            } else {
                left = (left << 1) | (left >>> 27);
                right = (right << 1) | (right >>> 27);
            }
            left &= -0xf;
            right &= -0xf;

            // now apply PC-2, in such a way that E is easier when encrypting or decrypting
            // this conversion will look like PC-2 except only the last 6 bits of each byte are used
            // rather than 48 consecutive bits and the order of lines will be according to
            // how the S selection functions will be applied: S2, S4, S6, S8, S1, S3, S5, S7
            lefttemp =
                pc2bytes0[left >>> 28] |
                pc2bytes1[(left >>> 24) & 0xf] |
                pc2bytes2[(left >>> 20) & 0xf] |
                pc2bytes3[(left >>> 16) & 0xf] |
                pc2bytes4[(left >>> 12) & 0xf] |
                pc2bytes5[(left >>> 8) & 0xf] |
                pc2bytes6[(left >>> 4) & 0xf];
            righttemp =
                pc2bytes7[right >>> 28] |
                pc2bytes8[(right >>> 24) & 0xf] |
                pc2bytes9[(right >>> 20) & 0xf] |
                pc2bytes10[(right >>> 16) & 0xf] |
                pc2bytes11[(right >>> 12) & 0xf] |
                pc2bytes12[(right >>> 8) & 0xf] |
                pc2bytes13[(right >>> 4) & 0xf];
            temp = ((righttemp >>> 16) ^ lefttemp) & 0x0000ffff;
            keys[n++] = lefttemp ^ temp;
            keys[n++] = righttemp ^ (temp << 16);
        }
    } // for each iterations
    // return the keys we've created
    return keys;
} // end of des_createKeys

// //////////////////////////// TEST //////////////////////////////
/*
function stringToHex (s) {
  var r = "0x";
  var hexes = new Array ("0","1","2","3","4","5","6","7","8","9","a","b","c","d","e","f");
  for (var i=0; i<s.length; i++) {r += hexes [s.charCodeAt(i) >> 4] + hexes [s.charCodeAt(i) & 0xf];}
  return r;
}

var key = "this is a 24 byte key !!";
var message = "This is a test message";
var ciphertext = des (key, message, 1, 0);
document.writeln ("DES Test: " + stringToHex (ciphertext));
*/

/*---------------------------------------------
 * Other libs - RSA and PRNG
 *---------------------------------------------*/
/*
 * Copyright (c) 2003-2005  Tom Wu
 * All Rights Reserved.
 *
 * Permission is hereby granted, free of charge, to any person obtaining
 * a copy of this software and associated documentation files (the
 * "Software"), to deal in the Software without restriction, including
 * without limitation the rights to use, copy, modify, merge, publish,
 * distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so, subject to
 * the following conditions:
 *
 * The above copyright notice and this permission notice shall be
 * included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS-IS" AND WITHOUT WARRANTY OF ANY KIND,
 * EXPRESS, IMPLIED OR OTHERWISE, INCLUDING WITHOUT LIMITATION, ANY
 * WARRANTY OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE.
 *
 * IN NO EVENT SHALL TOM WU BE LIABLE FOR ANY SPECIAL, INCIDENTAL,
 * INDIRECT OR CONSEQUENTIAL DAMAGES OF ANY KIND, OR ANY DAMAGES WHATSOEVER
 * RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER OR NOT ADVISED OF
 * THE POSSIBILITY OF DAMAGE, AND ON ANY THEORY OF LIABILITY, ARISING OUT
 * OF OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 *
 * In addition, the following condition applies:
 *
 * All redistributions must retain an intact copy of this copyright notice
 * and disclaimer.
 */

// Copyright (c) 2005  Tom Wu
// All Rights Reserved.
// See "LICENSE" for details.

// Basic JavaScript BN library - subset useful for RSA encryption.

// Bits per digit
let dbits;

// JavaScript engine analysis
const canary = 0xdeadbeefcafe;
const j_lm = (canary & 0xffffff) === 0xefcafe;

// (public) Constructor
// @ts-ignore
function BigInteger(a, b, c) {
    if (a != null) {
        // @ts-ignore
        if (typeof a === 'number') this.fromNumber(a, b, c);
        // @ts-ignore
        else if (b == null && typeof a !== 'string') this.fromString(a, 256);
        // @ts-ignore
        else this.fromString(a, b);
    }
}

// return new, unset BigInteger
function nbi() {
    // @ts-ignore
    return new BigInteger(null);
}

// am: Compute w_j += (x*this_i), propagate carries,
// c is initial carry, returns final carry.
// c < 3*dvalue, x < 2*dvalue, this_i < dvalue
// We need to select the fastest one that works in this environment.

// am1: use a single mult and divide to get the high bits,
// max digit bits should be 26 because
// max internal value = 2*dvalue^2-2*dvalue (< 2^53)
// @ts-ignore
function am1(i, x, w, j, c, n) {
    while (--n >= 0) {
        // @ts-ignore
        const v = x * this[i++] + w[j] + c;
        c = Math.floor(v / 0x4000000);
        w[j++] = v & 0x3ffffff;
    }
    return c;
}

// am2 avoids a big mult-and-extract completely.
// Max digit bits should be <= 30 because we do bitwise ops
// on values up to 2*hdvalue^2-hdvalue-1 (< 2^31)
// @ts-ignore
function am2(i, x, w, j, c, n) {
    const xl = x & 0x7fff;
    const xh = x >> 15;
    while (--n >= 0) {
        // @ts-ignore
        let l = this[i] & 0x7fff;
        // @ts-ignore
        const h = this[i++] >> 15;
        const m = xh * l + h * xl;
        l = xl * l + ((m & 0x7fff) << 15) + w[j] + (c & 0x3fffffff);
        c = (l >>> 30) + (m >>> 15) + xh * h + (c >>> 30);
        w[j++] = l & 0x3fffffff;
    }
    return c;
}

// Alternately, set max digit bits to 28 since some
// browsers slow down when dealing with 32-bit numbers.
// @ts-ignore
function am3(i, x, w, j, c, n) {
    const xl = x & 0x3fff;
    const xh = x >> 14;
    while (--n >= 0) {
        // @ts-ignore
        let l = this[i] & 0x3fff;
        // @ts-ignore
        const h = this[i++] >> 14;
        const m = xh * l + h * xl;
        l = xl * l + ((m & 0x3fff) << 14) + w[j] + c;
        c = (l >> 28) + (m >> 14) + xh * h;
        w[j++] = l & 0xfffffff;
    }
    return c;
}

if (j_lm && navigator.appName === 'Microsoft Internet Explorer') {
    BigInteger.prototype.am = am2;
    dbits = 30;
} else if (j_lm && navigator.appName !== 'Netscape') {
    BigInteger.prototype.am = am1;
    dbits = 26;
} else {
    // Mozilla/Netscape seems to prefer am3
    BigInteger.prototype.am = am3;
    dbits = 28;
}

BigInteger.prototype.DB = dbits;
BigInteger.prototype.DM = (1 << dbits) - 1;
BigInteger.prototype.DV = 1 << dbits;

const BI_FP = 52;
BigInteger.prototype.FV = Math.pow(2, BI_FP);
BigInteger.prototype.F1 = BI_FP - dbits;
BigInteger.prototype.F2 = 2 * dbits - BI_FP;

// Digit conversions
const BI_RM = '0123456789abcdefghijklmnopqrstuvwxyz';
// @ts-ignore
const BI_RC = [];
let rr;
let vv;
rr = '0'.charCodeAt(0);
// @ts-ignore
for (vv = 0; vv <= 9; ++vv) BI_RC[rr++] = vv;
rr = 'a'.charCodeAt(0);
// @ts-ignore
for (vv = 10; vv < 36; ++vv) BI_RC[rr++] = vv;
rr = 'A'.charCodeAt(0);
// @ts-ignore
for (vv = 10; vv < 36; ++vv) BI_RC[rr++] = vv;

function int2char(n: any) {
    return BI_RM.charAt(n);
}

function intAt(s: any, i: any) {
    // @ts-ignore
    const c = BI_RC[s.charCodeAt(i)];
    return c == null ? -1 : c;
}

// (protected) copy this to r
function bnpCopyTo(r: any) {
    // @ts-ignore
    for (let i = this.t - 1; i >= 0; --i) r[i] = this[i];
    // @ts-ignore
    r.t = this.t;
    // @ts-ignore
    r.s = this.s;
}

// (protected) set from integer value x, -DV <= x < DV
function bnpFromInt(x: any) {
    // @ts-ignore
    this.t = 1;
    // @ts-ignore
    this.s = x < 0 ? -1 : 0;
    // @ts-ignore
    if (x > 0) this[0] = x;
    // @ts-ignore
    else if (x < -1) this[0] = x + DV;
    // @ts-ignore
    else this.t = 0;
}

// return bigint initialized to value
function nbv(i: any) {
    const r = nbi();
    r.fromInt(i);
    return r;
}

// (protected) set from string and radix
function bnpFromString(s: any, b: any) {
    let k;
    if (b === 16) k = 4;
    else if (b === 8) k = 3;
    else if (b === 256) k = 8;
    // byte array
    else if (b === 2) k = 1;
    else if (b === 32) k = 5;
    else if (b === 4) k = 2;
    else {
        // @ts-ignore
        this.fromRadix(s, b);
        return;
    }
    // @ts-ignore
    this.t = 0;
    // @ts-ignore
    this.s = 0;
    let i = s.length;
    let mi = false;
    let sh = 0;
    while (--i >= 0) {
        const x = k === 8 ? s[i] & 0xff : intAt(s, i);
        if (x < 0) {
            if (s.charAt(i) === '-') mi = true;
            continue;
        }
        mi = false;
        // @ts-ignore
        if (sh === 0) this[this.t++] = x;
        // @ts-ignore
        else if (sh + k > this.DB) {
            // @ts-ignore
            this[this.t - 1] |= (x & ((1 << (this.DB - sh)) - 1)) << sh;
            // @ts-ignore
            this[this.t++] = x >> (this.DB - sh);
            // @ts-ignore
        } else this[this.t - 1] |= x << sh;
        sh += k;
        // @ts-ignore
        if (sh >= this.DB) sh -= this.DB;
    }
    if (k === 8 && (s[0] & 0x80) !== 0) {
        // @ts-ignore
        this.s = -1;
        // @ts-ignore
        if (sh > 0) this[this.t - 1] |= ((1 << (this.DB - sh)) - 1) << sh;
    }
    // @ts-ignore
    this.clamp();
    // @ts-ignore
    if (mi) BigInteger.ZERO.subTo(this, this);
}

// (protected) clamp off excess high words
function bnpClamp() {
    // @ts-ignore
    const c = this.s & this.DM;
    // @ts-ignore
    while (this.t > 0 && this[this.t - 1] === c) --this.t;
}

// (public) return string representation in given radix
function bnToString(b: any) {
    // @ts-ignore
    if (this.s < 0) return `-${this.negate().toString(b)}`;
    let k;
    if (b === 16) k = 4;
    else if (b === 8) k = 3;
    else if (b === 2) k = 1;
    else if (b === 32) k = 5;
    else if (b === 4) k = 2;
    // @ts-ignore
    else return this.toRadix(b);
    const km = (1 << k) - 1;
    let d;
    let m = false;
    let r = '';
    // @ts-ignore
    let i = this.t;
    // @ts-ignore
    let p = this.DB - ((i * this.DB) % k);
    if (i-- > 0) {
        // @ts-ignore
        if (p < this.DB && (d = this[i] >> p) > 0) {
            m = true;
            r = int2char(d);
        }
        while (i >= 0) {
            if (p < k) {
                // @ts-ignore
                d = (this[i] & ((1 << p) - 1)) << (k - p);
                // @ts-ignore
                d |= this[--i] >> (p += this.DB - k);
            } else {
                // @ts-ignore
                d = (this[i] >> (p -= k)) & km;
                if (p <= 0) {
                    // @ts-ignore
                    p += this.DB;
                    --i;
                }
            }
            if (d > 0) m = true;
            if (m) r += int2char(d);
        }
    }
    return m ? r : '0';
}

// (public) -this
function bnNegate() {
    const r = nbi();
    // @ts-ignore
    BigInteger.ZERO.subTo(this, r);
    return r;
}

// (public) |this|
function bnAbs() {
    // @ts-ignore
    return this.s < 0 ? this.negate() : this;
}

// (public) return + if this > a, - if this < a, 0 if equal
function bnCompareTo(a: any) {
    // @ts-ignore
    let r = this.s - a.s;
    if (r !== 0) return r;
    // @ts-ignore
    let i = this.t;
    r = i - a.t;
    if (r !== 0) return r;
    // @ts-ignore
    while (--i >= 0) if ((r = this[i] - a[i]) !== 0) return r;
    return 0;
}

// returns bit length of the integer x
function nbits(x: any) {
    let r = 1;
    let t;
    if ((t = x >>> 16) !== 0) {
        x = t;
        r += 16;
    }
    if ((t = x >> 8) !== 0) {
        x = t;
        r += 8;
    }
    if ((t = x >> 4) !== 0) {
        x = t;
        r += 4;
    }
    if ((t = x >> 2) !== 0) {
        x = t;
        r += 2;
    }
    if ((t = x >> 1) !== 0) {
        x = t;
        r += 1;
    }
    return r;
}

// (public) return the number of bits in "this"
function bnBitLength() {
    // @ts-ignore
    if (this.t <= 0) return 0;
    // @ts-ignore
    return this.DB * (this.t - 1) + nbits(this[this.t - 1] ^ (this.s & this.DM));
}

// (protected) r = this << n*DB
function bnpDLShiftTo(n: any, r: any) {
    let i;
    // @ts-ignore
    for (i = this.t - 1; i >= 0; --i) r[i + n] = this[i];
    for (i = n - 1; i >= 0; --i) r[i] = 0;
    // @ts-ignore
    r.t = this.t + n;
    // @ts-ignore
    r.s = this.s;
}

// (protected) r = this >> n*DB
function bnpDRShiftTo(n: any, r: any) {
    // @ts-ignore
    for (let i = n; i < this.t; ++i) r[i - n] = this[i];
    // @ts-ignore
    r.t = Math.max(this.t - n, 0);
    // @ts-ignore
    r.s = this.s;
}

// (protected) r = this << n
function bnpLShiftTo(n: any, r: any) {
    // @ts-ignore
    const bs = n % this.DB;
    // @ts-ignore
    const cbs = this.DB - bs;
    const bm = (1 << cbs) - 1;
    // @ts-ignore
    const ds = Math.floor(n / this.DB);
    // @ts-ignore
    let c = (this.s << bs) & this.DM;
    let i;
    // @ts-ignore
    for (i = this.t - 1; i >= 0; --i) {
        // @ts-ignore
        r[i + ds + 1] = (this[i] >> cbs) | c;
        // @ts-ignore
        c = (this[i] & bm) << bs;
    }
    for (i = ds - 1; i >= 0; --i) r[i] = 0;
    r[ds] = c;
    // @ts-ignore
    r.t = this.t + ds + 1;
    // @ts-ignore
    r.s = this.s;
    r.clamp();
}

// (protected) r = this >> n
function bnpRShiftTo(n: any, r: any) {
    // @ts-ignore
    r.s = this.s;
    // @ts-ignore
    const ds = Math.floor(n / this.DB);
    // @ts-ignore
    if (ds >= this.t) {
        r.t = 0;
        return;
    }
    // @ts-ignore
    const bs = n % this.DB;
    // @ts-ignore
    const cbs = this.DB - bs;
    const bm = (1 << bs) - 1;
    // @ts-ignore
    r[0] = this[ds] >> bs;
    // @ts-ignore
    for (let i = ds + 1; i < this.t; ++i) {
        // @ts-ignore
        r[i - ds - 1] |= (this[i] & bm) << cbs;
        // @ts-ignore
        r[i - ds] = this[i] >> bs;
    }
    // @ts-ignore
    if (bs > 0) r[this.t - ds - 1] |= (this.s & bm) << cbs;
    // @ts-ignore
    r.t = this.t - ds;
    r.clamp();
}

// (protected) r = this - a
function bnpSubTo(a: any, r: any) {
    let i = 0;
    let c = 0;
    // @ts-ignore
    const m = Math.min(a.t, this.t);
    while (i < m) {
        // @ts-ignore
        c += this[i] - a[i];
        // @ts-ignore
        r[i++] = c & this.DM;
        // @ts-ignore
        c >>= this.DB;
    }
    // @ts-ignore
    if (a.t < this.t) {
        c -= a.s;
        // @ts-ignore
        while (i < this.t) {
            // @ts-ignore
            c += this[i];
            // @ts-ignore
            r[i++] = c & this.DM;
            // @ts-ignore
            c >>= this.DB;
        }
        // @ts-ignore
        c += this.s;
    } else {
        // @ts-ignore
        c += this.s;
        while (i < a.t) {
            c -= a[i];
            // @ts-ignore
            r[i++] = c & this.DM;
            // @ts-ignore
            c >>= this.DB;
        }
        c -= a.s;
    }
    r.s = c < 0 ? -1 : 0;
    // @ts-ignore
    if (c < -1) r[i++] = this.DV + c;
    else if (c > 0) r[i++] = c;
    r.t = i;
    r.clamp();
}

// (protected) r = this * a, r != this,a (HAC 14.12)
// "this" should be the larger one if appropriate.
function bnpMultiplyTo(a: any, r: any) {
    // @ts-ignore
    const x = this.abs();
    const y = a.abs();
    let i = x.t;
    r.t = i + y.t;
    while (--i >= 0) r[i] = 0;
    for (i = 0; i < y.t; ++i) r[i + x.t] = x.am(0, y[i], r, i, 0, x.t);
    r.s = 0;
    r.clamp();
    // @ts-ignore
    if (this.s !== a.s) BigInteger.ZERO.subTo(r, r);
}

// (protected) r = this^2, r != this (HAC 14.16)
function bnpSquareTo(r: any) {
    // @ts-ignore
    const x = this.abs();
    let i = (r.t = 2 * x.t);
    while (--i >= 0) r[i] = 0;
    for (i = 0; i < x.t - 1; ++i) {
        const c = x.am(i, x[i], r, 2 * i, 0, 1);
        if ((r[i + x.t] += x.am(i + 1, 2 * x[i], r, 2 * i + 1, c, x.t - i - 1)) >= x.DV) {
            r[i + x.t] -= x.DV;
            r[i + x.t + 1] = 1;
        }
    }
    if (r.t > 0) r[r.t - 1] += x.am(i, x[i], r, 2 * i, 0, 1);
    r.s = 0;
    r.clamp();
}

// (protected) divide this by m, quotient and remainder to q, r (HAC 14.20)
// r != q, this != m.  q or r may be null.
function bnpDivRemTo(m: any, q: any, r: any) {
    const pm = m.abs();
    if (pm.t <= 0) return;
    // @ts-ignore
    const pt = this.abs();
    if (pt.t < pm.t) {
        if (q != null) q.fromInt(0);
        // @ts-ignore
        if (r != null) this.copyTo(r);
        return;
    }
    if (r == null) r = nbi();
    const y = nbi();
    // @ts-ignore
    const ts = this.s;
    const ms = m.s;
    // @ts-ignore
    const nsh = this.DB - nbits(pm[pm.t - 1]); // normalize modulus
    if (nsh > 0) {
        pm.lShiftTo(nsh, y);
        pt.lShiftTo(nsh, r);
    } else {
        pm.copyTo(y);
        pt.copyTo(r);
    }
    const ys = y.t;
    const y0 = y[ys - 1];
    if (y0 === 0) return;
    // @ts-ignore
    const yt = y0 * (1 << this.F1) + (ys > 1 ? y[ys - 2] >> this.F2 : 0);
    // @ts-ignore
    const d1 = this.FV / yt;
    // @ts-ignore
    const d2 = (1 << this.F1) / yt;
    // @ts-ignore
    const e = 1 << this.F2;
    let i = r.t;
    let j = i - ys;
    const t = q == null ? nbi() : q;
    y.dlShiftTo(j, t);
    if (r.compareTo(t) >= 0) {
        r[r.t++] = 1;
        r.subTo(t, r);
    }
    BigInteger.ONE.dlShiftTo(ys, t);
    t.subTo(y, y); // "negative" y so we can replace sub with am later
    while (y.t < ys) y[y.t++] = 0;
    while (--j >= 0) {
        // Estimate quotient digit
        // @ts-ignore
        let qd = r[--i] === y0 ? this.DM : Math.floor(r[i] * d1 + (r[i - 1] + e) * d2);
        if ((r[i] += y.am(0, qd, r, j, 0, ys)) < qd) {
            // Try it out
            y.dlShiftTo(j, t);
            r.subTo(t, r);
            while (r[i] < --qd) r.subTo(t, r);
        }
    }
    if (q != null) {
        r.drShiftTo(ys, q);
        if (ts !== ms) BigInteger.ZERO.subTo(q, q);
    }
    r.t = ys;
    r.clamp();
    if (nsh > 0) r.rShiftTo(nsh, r); // Denormalize remainder
    if (ts < 0) BigInteger.ZERO.subTo(r, r);
}

// (public) this mod a
function bnMod(a: any) {
    const r = nbi();
    // @ts-ignore
    this.abs().divRemTo(a, null, r);
    // @ts-ignore
    if (this.s < 0 && r.compareTo(BigInteger.ZERO) > 0) a.subTo(r, r);
    return r;
}

// Modular reduction using "classic" algorithm
function Classic(m: any) {
    // @ts-ignore
    this.m = m;
}

function cConvert(x: any) {
    // @ts-ignore
    if (x.s < 0 || x.compareTo(this.m) >= 0) return x.mod(this.m);
    return x;
}

function cRevert(x: any) {
    return x;
}

function cReduce(x: any) {
    // @ts-ignore
    x.divRemTo(this.m, null, x);
}

function cMulTo(x: any, y: any, r: any) {
    x.multiplyTo(y, r);
    // @ts-ignore
    this.reduce(r);
}

function cSqrTo(x: any, r: any) {
    x.squareTo(r);
    // @ts-ignore
    this.reduce(r);
}

Classic.prototype.convert = cConvert;
Classic.prototype.revert = cRevert;
Classic.prototype.reduce = cReduce;
Classic.prototype.mulTo = cMulTo;
Classic.prototype.sqrTo = cSqrTo;

// (protected) return "-1/this % 2^DB"; useful for Mont. reduction
// justification:
//         xy == 1 (mod m)
//         xy =  1+km
//   xy(2-xy) = (1+km)(1-km)
// x[y(2-xy)] = 1-k^2m^2
// x[y(2-xy)] == 1 (mod m^2)
// if y is 1/x mod m, then y(2-xy) is 1/x mod m^2
// should reduce x and y(2-xy) by m^2 at each step to keep size bounded.
// JS multiply "overflows" differently from C/C++, so care is needed here.
function bnpInvDigit() {
    // @ts-ignore
    if (this.t < 1) return 0;
    // @ts-ignore
    const x = this[0];
    if ((x & 1) === 0) return 0;
    let y = x & 3; // y == 1/x mod 2^2
    y = (y * (2 - (x & 0xf) * y)) & 0xf; // y == 1/x mod 2^4
    y = (y * (2 - (x & 0xff) * y)) & 0xff; // y == 1/x mod 2^8
    y = (y * (2 - (((x & 0xffff) * y) & 0xffff))) & 0xffff; // y == 1/x mod 2^16
    // last step - calculate inverse mod DV directly;
    // assumes 16 < DB <= 32 and assumes ability to handle 48-bit ints
    // @ts-ignore
    y = (y * (2 - ((x * y) % this.DV))) % this.DV; // y == 1/x mod 2^dbits
    // we really want the negative inverse, and -DV < y < DV
    // @ts-ignore
    return y > 0 ? this.DV - y : -y;
}

// Montgomery reduction
function Montgomery(m: any) {
    // @ts-ignore
    this.m = m;
    // @ts-ignore
    this.mp = m.invDigit();
    // @ts-ignore
    this.mpl = this.mp & 0x7fff;
    // @ts-ignore
    this.mph = this.mp >> 15;
    // @ts-ignore
    this.um = (1 << (m.DB - 15)) - 1;
    // @ts-ignore
    this.mt2 = 2 * m.t;
}

// xR mod m
function montConvert(x: any) {
    const r = nbi();
    // @ts-ignore
    x.abs().dlShiftTo(this.m.t, r);
    // @ts-ignore
    r.divRemTo(this.m, null, r);
    // @ts-ignore
    if (x.s < 0 && r.compareTo(BigInteger.ZERO) > 0) this.m.subTo(r, r);
    return r;
}

// x/R mod m
function montRevert(x: any) {
    const r = nbi();
    x.copyTo(r);
    // @ts-ignore
    this.reduce(r);
    return r;
}

// x = x/R mod m (HAC 14.32)
function montReduce(x: any) {
    while (
        // @ts-ignore
        x.t <= this.mt2 // pad x so am has enough room later
    )
        x[x.t++] = 0;
    // @ts-ignore
    for (let i = 0; i < this.m.t; ++i) {
        // faster way of calculating u0 = x[i]*mp mod DV
        let j = x[i] & 0x7fff;

        const u0 =
            // @ts-ignore
            (j * this.mpl + (((j * this.mph + (x[i] >> 15) * this.mpl) & this.um) << 15)) & x.DM;
        // use am to combine the multiply-shift-add into one call
        // @ts-ignore
        j = i + this.m.t;
        // @ts-ignore
        x[j] += this.m.am(0, u0, x, i, 0, this.m.t);
        // propagate carry
        while (x[j] >= x.DV) {
            x[j] -= x.DV;
            x[++j]++;
        }
    }
    x.clamp();
    // @ts-ignore
    x.drShiftTo(this.m.t, x);
    // @ts-ignore
    if (x.compareTo(this.m) >= 0) x.subTo(this.m, x);
}

// r = "x^2/R mod m"; x != r
function montSqrTo(x: any, r: any) {
    x.squareTo(r);
    // @ts-ignore
    this.reduce(r);
}

// r = "xy/R mod m"; x,y != r
function montMulTo(x: any, y: any, r: any) {
    x.multiplyTo(y, r);
    // @ts-ignore
    this.reduce(r);
}

Montgomery.prototype.convert = montConvert;
Montgomery.prototype.revert = montRevert;
Montgomery.prototype.reduce = montReduce;
Montgomery.prototype.mulTo = montMulTo;
Montgomery.prototype.sqrTo = montSqrTo;

// (protected) true iff this is even
function bnpIsEven() {
    // @ts-ignore
    return (this.t > 0 ? this[0] & 1 : this.s) === 0;
}

// (protected) this^e, e < 2^32, doing sqr and mul with "r" (HAC 14.79)
function bnpExp(e: any, z: any) {
    if (e > 0xffffffff || e < 1) return BigInteger.ONE;
    let r = nbi();
    let r2 = nbi();
    // @ts-ignore
    const g = z.convert(this);
    let i = nbits(e) - 1;
    g.copyTo(r);
    while (--i >= 0) {
        z.sqrTo(r, r2);
        if ((e & (1 << i)) > 0) z.mulTo(r2, g, r);
        else {
            const t = r;
            r = r2;
            r2 = t;
        }
    }
    return z.revert(r);
}

// (public) this^e % m, 0 <= e < 2^32
function bnModPowInt(e: any, m: any) {
    let z;
    // @ts-ignore
    if (e < 256 || m.isEven()) z = new Classic(m);
    // @ts-ignore
    else z = new Montgomery(m);
    // @ts-ignore
    return this.exp(e, z);
}

// protected
BigInteger.prototype.copyTo = bnpCopyTo;
BigInteger.prototype.fromInt = bnpFromInt;
BigInteger.prototype.fromString = bnpFromString;
BigInteger.prototype.clamp = bnpClamp;
BigInteger.prototype.dlShiftTo = bnpDLShiftTo;
BigInteger.prototype.drShiftTo = bnpDRShiftTo;
BigInteger.prototype.lShiftTo = bnpLShiftTo;
BigInteger.prototype.rShiftTo = bnpRShiftTo;
BigInteger.prototype.subTo = bnpSubTo;
BigInteger.prototype.multiplyTo = bnpMultiplyTo;
BigInteger.prototype.squareTo = bnpSquareTo;
BigInteger.prototype.divRemTo = bnpDivRemTo;
BigInteger.prototype.invDigit = bnpInvDigit;
BigInteger.prototype.isEven = bnpIsEven;
BigInteger.prototype.exp = bnpExp;

// public
BigInteger.prototype.toString = bnToString;
BigInteger.prototype.negate = bnNegate;
BigInteger.prototype.abs = bnAbs;
BigInteger.prototype.compareTo = bnCompareTo;
BigInteger.prototype.bitLength = bnBitLength;
BigInteger.prototype.mod = bnMod;
BigInteger.prototype.modPowInt = bnModPowInt;

// "constants"
BigInteger.ZERO = nbv(0);
BigInteger.ONE = nbv(1);

// Random number generator - requires a PRNG backend, e.g. prng4.js

// For best results, put code like
// <body onClick='rng_seed_time();' onKeyPress='rng_seed_time();'>
// in your main HTML document.
// @ts-ignore
let rng_state;
// @ts-ignore
let rng_pool;
// @ts-ignore
let rng_pptr;

// Pool size must be a multiple of 4 and greater than 32.
// An array of bytes the size of the pool will be passed to init()
var rng_psize = 256;

// Mix in a 32-bit integer into the pool
function rng_seed_int(x: any) {
    // @ts-ignore
    rng_pool[rng_pptr++] ^= x & 255;
    // @ts-ignore
    rng_pool[rng_pptr++] ^= (x >> 8) & 255;
    // @ts-ignore
    rng_pool[rng_pptr++] ^= (x >> 16) & 255;
    // @ts-ignore
    rng_pool[rng_pptr++] ^= (x >> 24) & 255;
    // @ts-ignore
    if (rng_pptr >= rng_psize) rng_pptr -= rng_psize;
}

// Mix in the current time (w/milliseconds) into the pool
function rng_seed_time() {
    init();
    rng_seed_int(new Date().getTime());
}

const init = () => {
    // Initialize the pool with junk if needed.
    if (rng_pool == null) {
        rng_pool = [];
        rng_pptr = 0;
        let t;
        if (navigator.appName === 'Netscape' && navigator.appVersion < '5' && window.crypto) {
            // Extract entropy (256 bits) from NS4 RNG if available
            // @ts-ignore
            const array = new Uint32Array(32);
            const z = window.crypto.getRandomValues(array);
            for (t = 0; t < z.length; ++t) rng_pool[rng_pptr++] = z[t] & 255;
        }
        // @ts-ignore
        while (rng_pptr < rng_psize) {
            // extract some randomness from Math.random()
            t = Math.floor(65536 * Math.random());
            rng_pool[rng_pptr++] = t >>> 8;
            rng_pool[rng_pptr++] = t & 255;
        }
        rng_pptr = 0;
        rng_seed_time();
        // rng_seed_int(window.screenX);
        // rng_seed_int(window.screenY);
    }
};

function rng_get_byte() {
    // @ts-ignore
    if (rng_state == null) {
        rng_seed_time();
        rng_state = prng_newstate();
        // @ts-ignore
        rng_state.init(rng_pool);
        for (rng_pptr = 0; rng_pptr < rng_pool.length; ++rng_pptr) rng_pool[rng_pptr] = 0;
        rng_pptr = 0;
        // rng_pool = null;
    }
    // TODO: allow reseeding after first request
    // @ts-ignore
    return rng_state.next();
}

function rng_get_bytes(ba: any) {
    let i;
    for (i = 0; i < ba.length; ++i) ba[i] = rng_get_byte();
}

function SecureRandom() {}

SecureRandom.prototype.nextBytes = rng_get_bytes;

// prng4.js - uses Arcfour as a PRNG

function Arcfour() {
    // @ts-ignore
    this.i = 0;
    // @ts-ignore
    this.j = 0;
    // @ts-ignore
    this.S = [];
}

// Initialize arcfour context from key, an array of ints, each from [0..255]
function ARC4init(key: any) {
    let i;
    let j;
    let t;
    // @ts-ignore
    for (i = 0; i < 256; ++i) this.S[i] = i;
    j = 0;
    for (i = 0; i < 256; ++i) {
        // @ts-ignore
        j = (j + this.S[i] + key[i % key.length]) & 255;
        // @ts-ignore
        t = this.S[i];
        // @ts-ignore
        this.S[i] = this.S[j];
        // @ts-ignore
        this.S[j] = t;
    }
    // @ts-ignore
    this.i = 0;
    // @ts-ignore
    this.j = 0;
}

function ARC4next() {
    let t;
    // @ts-ignore
    this.i = (this.i + 1) & 255;
    // @ts-ignore
    this.j = (this.j + this.S[this.i]) & 255;
    // @ts-ignore
    t = this.S[this.i];
    // @ts-ignore
    this.S[this.i] = this.S[this.j];
    // @ts-ignore
    this.S[this.j] = t;
    // @ts-ignore
    return this.S[(t + this.S[this.i]) & 255];
}

Arcfour.prototype.init = ARC4init;
Arcfour.prototype.next = ARC4next;

// Plug in your RNG constructor here
function prng_newstate() {
    // @ts-ignore
    return new Arcfour();
}

// Depends on jsbn.js and rng.js

// Version 1.1: support utf-8 encoding in pkcs1pad2

// convert a (hex) string to a bignum object
function parseBigInt(str: any, r: any) {
    // @ts-ignore
    return new BigInteger(str, r);
}

// PKCS#1 (type 2, random) pad input string s to n bytes, and return a bigint
function pkcs1pad2(s: any, n: any) {
    if (n < s.length + 11) {
        // TODO: fix for utf-8
        throw new Error('Message too long for RSA');
    }
    const ba = [];
    let i = s.length - 1;
    while (i >= 0 && n > 0) {
        const c = s.charCodeAt(i--);
        if (c < 128) {
            // encode using utf-8
            // @ts-ignore
            ba[--n] = c;
        } else if (c > 127 && c < 2048) {
            // @ts-ignore
            ba[--n] = (c & 63) | 128;
            // @ts-ignore
            ba[--n] = (c >> 6) | 192;
        } else {
            // @ts-ignore
            ba[--n] = (c & 63) | 128;
            // @ts-ignore
            ba[--n] = ((c >> 6) & 63) | 128;
            // @ts-ignore
            ba[--n] = (c >> 12) | 224;
        }
    }
    // @ts-ignore
    ba[--n] = 0;
    // @ts-ignore
    const rng = new SecureRandom();
    const x = [];
    while (n > 2) {
        // random non-zero pad
        // @ts-ignore
        x[0] = 0;
        while (x[0] === 0) rng.nextBytes(x);
        ba[--n] = x[0];
    }
    // @ts-ignore
    ba[--n] = 2;
    // @ts-ignore
    ba[--n] = 0;
    // @ts-ignore
    return new BigInteger(ba);
}

// "empty" RSA key constructor
function RSAKey() {
    // @ts-ignore
    this.n = null;
    // @ts-ignore
    this.e = 0;
    // @ts-ignore
    this.d = null;
    // @ts-ignore
    this.p = null;
    // @ts-ignore
    this.q = null;
    // @ts-ignore
    this.dmp1 = null;
    // @ts-ignore
    this.dmq1 = null;
    // @ts-ignore
    this.coeff = null;
}

// Set the public key fields N and e from hex strings
function RSASetPublic(N: any, E: any) {
    if (N != null && E != null && N.length > 0 && E.length > 0) {
        // @ts-ignore
        this.n = parseBigInt(N, 16);
        // @ts-ignore
        this.e = parseInt(E, 16);
    } else throw new Error('Invalid RSA public key');
}

// Perform raw public operation on "x": return x^e (mod n)
function RSADoPublic(x: any) {
    // @ts-ignore
    return x.modPowInt(this.e, this.n);
}

// Return the PKCS#1 RSA encryption of "text" as an even-length hex string
function RSAEncrypt(text: any) {
    // @ts-ignore
    const m = pkcs1pad2(text, (this.n.bitLength() + 7) >> 3);
    if (m == null) return null;
    // @ts-ignore
    const c = this.doPublic(m);
    if (c == null) return null;
    const h = c.toString(16);
    if ((h.length & 1) === 0) return h;
    return `0${h}`;
}

// Return the PKCS#1 RSA encryption of "text" as a Base64-encoded string
// function RSAEncryptB64(text) {
//  var h = this.encrypt(text);
//  if(h) return hex2b64(h); else return null;
// }

// protected
RSAKey.prototype.doPublic = RSADoPublic;

// public
RSAKey.prototype.setPublic = RSASetPublic;
RSAKey.prototype.encrypt = RSAEncrypt;
// RSAKey.prototype.encrypt_b64 = RSAEncryptB64;
